import { useBlocker } from '@tanstack/react-router';
import React, { useEffect, useCallback } from 'react';
import { UseFormReturn, FormProvider as Form } from 'react-hook-form';

import Dialog from '@mui/material/Dialog';
import Button from '@mui/material/Button';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogContentText from '@mui/material/DialogContentText';

// ----------------------------------------------------------------------

/**
 * @typedef Props
 * @property {React.ReactNode} children - The child components to be rendered within the form provider.
 * @property {UseFormReturn<any>} methods - The methods returned by the `useForm` hook.
 * @property {VoidFunction} [onSubmit] - Optional function to be called when the form is submitted.
 * @property {VoidFunction} [onClose] - Optional function to be called when the form is closed.
 * @property {boolean} [enableUnsavedWarning] - If true, a warning will be shown for unsaved changes.
 * When this is enabled, the form state needs to be reset before navigating away, and any mutation
 * needs to be awaited before calling navigate.
 */
type Props = {
  children: React.ReactNode;
  methods: UseFormReturn<any>;
  onSubmit?: VoidFunction;
  onClose?: VoidFunction;
  enableUnsavedWarning?: boolean;
  id?: string;
};

/**
 * Provides a form context to be used by react-hook-form with TanStack Router navigation blocking.
 * @property {React.ReactNode} children - The child components to be rendered within the form provider.
 * @property {UseFormReturn<any>} methods - The methods returned by the `useForm` hook.
 * @property {VoidFunction} [onSubmit] - Optional function to be called when the form is submitted.
 * @property {VoidFunction} [onClose] - Optional function to be called when the form is closed.
 * @property {boolean} [enableUnsavedWarning] - If true, a warning will be shown for unsaved changes.
 * When this is enabled, the form state needs to be reset before navigating away, and any mutation
 * needs to be awaited before calling navigate.
 */
function FormProvider({ children, onSubmit, methods, enableUnsavedWarning = false, id }: Props) {
  const { formState } = methods;
  const { isDirty, isSubmitting } = formState;
  const [isBlocked, setIsBlocked] = React.useState(false);

  // TanStack Router implementation
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) =>
      isDirty && !isSubmitting && current.pathname !== next.pathname,
    withResolver: true,
    disabled: !enableUnsavedWarning,
  });

  const handleProceed = useCallback(() => {
    if (blocker.status === 'blocked') {
      blocker.proceed();
    }
    setIsBlocked(false);
  }, [blocker]);

  const handleReset = useCallback(() => {
    if (blocker.status === 'blocked') {
      blocker.reset();
    }
    setIsBlocked(false);
  }, [blocker]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (enableUnsavedWarning && isDirty && !isSubmitting) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enableUnsavedWarning, isDirty, isSubmitting]);

  return (
    <Form {...methods}>
      <Dialog
        open={enableUnsavedWarning && (blocker.status === 'blocked' || isBlocked)}
        onClose={handleReset}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Unsaved Changes</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You have unsaved changes. Are you sure you want to leave this page?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={handleReset}>
            Stay on this Page
          </Button>
          <Button variant="contained" onClick={handleProceed} autoFocus>
            Leave Page
          </Button>
        </DialogActions>
      </Dialog>
      <form id={id} onSubmit={onSubmit}>
        {children}
      </form>
    </Form>
  );
}

export { FormProvider, type Props as FormProviderProps };
